import React, { useRef } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';

// Shared status filters for side panels. Tabs wrap rather than clipping labels
// and keep the same keyboard navigation whether the records are jobs or learnings.
export default function SidePanelTabs({ tabs, activeValue, onChange, counts = null, ariaLabel, idPrefix }) {
  const tabRefs = useRef([]);
  const valueOf = tab => tab.key ?? tab.label;
  const focusTab = index => {
    const bounded = (index + tabs.length) % tabs.length;
    const tab = tabs[bounded];
    if (!tab) return;
    onChange(valueOf(tab));
    tabRefs.current[bounded]?.focus();
  };
  const handleKeyDown = (event, index) => {
    const keys = {
      ArrowRight: () => focusTab(index + 1),
      ArrowLeft: () => focusTab(index - 1),
      Home: () => focusTab(0),
      End: () => focusTab(tabs.length - 1),
    };
    const handler = keys[event.key];
    if (handler) {
      event.preventDefault();
      handler();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onChange(valueOf(tabs[index]));
    }
  };

  return <Box mb={3} flexShrink={0}>
    <HStack role="tablist" aria-label={ariaLabel} spacing={0} borderBottom="1px solid" borderColor="gray.200" pb={0} flexWrap="wrap">
      {tabs.map((tab, index) => {
        const value = valueOf(tab);
        const isActive = activeValue === value;
        const count = counts ? counts[value] : null;
        return <Box
          as="button"
          type="button"
          key={value}
          ref={el => { tabRefs.current[index] = el; }}
          role="tab"
          id={`${idPrefix}-${String(value).replace(/\s+/g, '-').toLowerCase()}`}
          aria-selected={isActive}
          tabIndex={isActive ? 0 : -1}
          px={3} py={2}
          cursor="pointer"
          fontSize="xs"
          fontWeight={isActive ? '600' : '400'}
          color={isActive ? 'gray.800' : 'gray.500'}
          borderBottom="2px solid"
          borderColor={isActive ? 'gray.800' : 'transparent'}
          onClick={() => onChange(value)}
          onKeyDown={event => handleKeyDown(event, index)}
          _hover={{ color: 'gray.700' }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'blue.500', outlineOffset: '-2px' }}
          transition="all 0.15s"
          whiteSpace="nowrap"
        >
          {tab.label}
          {count > 0 && <Text as="span" ml={1.5} fontSize="10px" color={isActive ? 'gray.500' : 'gray.400'}>{count}</Text>}
        </Box>;
      })}
    </HStack>
  </Box>;
}
